import './App.css'
import { Alert, AlertTitle, Button, CssBaseline, Stack, ThemeProvider, Toolbar } from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { EntityTypesProvider } from './io/context/EntityTypes'
import LandingPage from './pages/LandingPage'
import buildEiduTheme from './ui/theme/eiduTheme'
import { createAuthContext } from './api/authorization/AuthenticationContext'
import TopAppBar from './components/layout/TopAppBar'
import { logAuthContext } from './util/Debug'
import { FormErrorProvider } from './util/FormErrorBus'
import { SettingsProvider, useSettings } from './io/context/Settings'
import { UserSettingsProvider } from './io/context/UserSettings'
import { AssistantContextProvider } from './components/assistant/AssistantContext'
import { getProductionOrganizationAccessError } from './config/productionOrganizationAccess'

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/
const DRAWER_WIDTH_PX = 240
const NAVIGATION_OVERLAY_MAX_VIEWPORT_WIDTH_PX = 900
const NAVIGATION_OVERLAY_WIDTH_RATIO_THRESHOLD = 0.2

const normalizeColorSetting = (value?: string) => {
  const trimmed = value?.trim()
  if (!trimmed) return undefined
  return HEX_COLOR_PATTERN.test(trimmed) ? trimmed : undefined
}

const pickColorSetting = (...values: Array<string | undefined>) =>
  values.map(normalizeColorSetting).find((value): value is string => !!value)

type ThemedAppProps = {
  authContext: ReturnType<typeof createAuthContext>
  signOut?: () => void
}

type ProductionOrgRestrictionPageProps = {
  message: string
  signOut?: () => void
}

const ProductionOrgRestrictionPage = ({ message, signOut }: ProductionOrgRestrictionPageProps) => (
  <Stack sx={{ minHeight: '100vh', alignItems: 'center', justifyContent: 'center', p: 2 }}>
    <Alert
      severity="error"
      sx={{ width: '100%', maxWidth: 720 }}
      action={
        signOut ? (
          <Button color="inherit" size="small" onClick={signOut}>
            Log out
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>Organization not allowed</AlertTitle>
      {message}
    </Alert>
  </Stack>
)

const ThemedApp = ({ authContext, signOut }: ThemedAppProps) => {
  const location = useLocation()
  const { getSettingValue } = useSettings()
  const backgroundColor = pickColorSetting(getSettingValue('Background Color'), getSettingValue('Backgound Color'))
  const textColor = pickColorSetting(getSettingValue('Text Color'))
  const organizationColor = pickColorSetting(getSettingValue('Organization Color'))
  const organizationTextColor = pickColorSetting(getSettingValue('Organization Text Color'))

  const isEmbedRoute = location.pathname.startsWith('/asset-embed/')
  const [navigationOpen, setNavigationOpen] = useState(false)
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth)

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const useOverlayNavigation =
    !isEmbedRoute &&
    viewportWidth <= NAVIGATION_OVERLAY_MAX_VIEWPORT_WIDTH_PX &&
    DRAWER_WIDTH_PX / Math.max(1, viewportWidth) > NAVIGATION_OVERLAY_WIDTH_RATIO_THRESHOLD

  useEffect(() => {
    if (!useOverlayNavigation) {
      setNavigationOpen(false)
    }
  }, [useOverlayNavigation])

  const theme = useMemo(
    () =>
      buildEiduTheme({
        backgroundColor,
        textColor,
        organizationColor,
        organizationTextColor,
      }),
    [backgroundColor, organizationColor, organizationTextColor, textColor]
  )

  const appSx = {
    bgcolor: 'background.default',
    color: 'text.primary',
    ...(isEmbedRoute ? { height: '100vh' } : {}),
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Stack className="app" sx={appSx}>
        {!isEmbedRoute && (
          <TopAppBar
            authContext={authContext}
            signOut={signOut}
            showNavigationMenuButton={useOverlayNavigation}
            onNavigationMenuClick={() => setNavigationOpen(true)}
          />
        )}
        {!isEmbedRoute && <Toolbar />}
        <Stack flex={1} sx={isEmbedRoute ? { minHeight: 0, height: '100%' } : undefined}>
          <LandingPage
            embedMode={isEmbedRoute}
            overlayNavigation={useOverlayNavigation}
            navigationOpen={navigationOpen}
            onNavigationClose={() => setNavigationOpen(false)}
          />
        </Stack>
      </Stack>
    </ThemeProvider>
  )
}

const App = () => {
  logAuthContext()
  const iAuthContext = useContext<IAuthContext>(AuthContext)
  const authContext = createAuthContext(iAuthContext)
  const { loginInProgress, tokenData, logOut } = iAuthContext
  const organizationId = iAuthContext.idTokenData?.organization_id
  const productionOrganizationAccessError = getProductionOrganizationAccessError({
    stage: import.meta.env.VITE_STAGE,
    organizationId,
  })

  const accessTokenExpired = tokenData?.exp === undefined || tokenData.exp * 1000 < Date.now()

  const appContent = () => {
    if (loginInProgress || accessTokenExpired) return null
    if (productionOrganizationAccessError) {
      return <ProductionOrgRestrictionPage message={productionOrganizationAccessError} signOut={logOut} />
    } else
      return (
        <EntityTypesProvider>
          <UserSettingsProvider>
            <SettingsProvider>
              <AssistantContextProvider>
                <ThemedApp authContext={authContext} signOut={logOut} />
              </AssistantContextProvider>
            </SettingsProvider>
          </UserSettingsProvider>
        </EntityTypesProvider>
      )
  }

  return <FormErrorProvider>{appContent()}</FormErrorProvider>
}

export default App
